import { useEffect, useMemo, useState, type ReactNode } from "react";
import { message } from "antd";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  DollarSign,
  Flame,
  FileText,
  AlertTriangle,
  Check,
  CloudUpload,
  Link as LinkIcon,
  Loader2,
  CheckCircle2,
  Send,
  Trash2,
  Wallet,
  RefreshCw,
  Plus,
  Search,
} from "lucide-react";

import { Link } from "react-router-dom";

import { Badge, badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCycleRange } from "@/lib/cycleRange";
import { countedQty, round2, taskFieldValue, toPerUnit, toTotal } from "@/lib/plannerPoints";
import { cn } from "@/lib/utils";
import { useTenantStore } from "@/stores/useTenantStore";
import { useArchivedTasks, useAssignees, useCatalog, useCreateTask, useDeleteTask, usePlan, useRestoreTask, useSetBudget, useSetTaskStatus, useTeamworkSync, useUpdateTask } from "@/hooks/usePlanner";
import { assigneeLabel, type CreateTaskInput, type PlannerTask } from "@/services/plannerService";
import { CollapsibleSection } from "@/components/planner/CollapsibleSection";
import { MobileTaskCard } from "@/components/planner/MobileTaskCard";
import { TaskDialog, type TaskDraft } from "@/components/planner/TaskDialog";
import { TaskNoteDialog } from "@/components/planner/TaskNoteDialog";
import { RunningNotesCard } from "@/components/planner/RunningNotesCard";
import { TaskFilter, applyRules, type FilterRule } from "@/components/planner/TaskFilter";
import { Checkbox } from "@/components/ui/checkbox";

const BUCKET_STYLE: Record<string, string> = {
  SEO: "text-coral-dark border-coral/40",
  "Google Ads": "text-teal-dark border-teal/50",
  "Meta Ads": "text-teal-dark border-teal/50",
  MISC: "text-muted-foreground border-border",
  "One-Time": "text-destructive border-destructive/40",
};

const PRIORITY_CHIP: Record<string, string> = {
  high: "bg-destructive/10 text-destructive",
  medium: "bg-coral-light text-coral-dark",
  low: "bg-teal-light text-teal-dark",
};

/** A stable empty list, so memos depending on it don't recompute each render. */
const NO_NAMES: string[] = [];

function shiftMonth(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** A create body minus null/undefined keys, so the backend's defaults apply
 *  (e.g. the catalog rate when no points are sent). */
function createBody(
  fields: Pick<CreateTaskInput, "billing_period" | "task_type"> & {
    [K in keyof CreateTaskInput]?: CreateTaskInput[K] | null;
  },
): CreateTaskInput {
  return Object.fromEntries(
    Object.entries(fields).filter(([, v]) => v !== undefined && v !== null),
  ) as unknown as CreateTaskInput; // the input type is checked; only the filter erases it
}

/** Media spend for an ad_spend row, or null when the row is labour.
 *
 *  The Planner's convention is 1 point == $100, and for ad_spend rows the
 *  money lives in points*qty. The table showed a bare qty, so Konala's Meta
 *  row read "112.5" when it means $11,250 — reported as a bug because that is
 *  exactly what it looks like. Mirrors _AD_SPEND_POINT_USD in planner_service. */
const AD_SPEND_POINT_USD = 100;
// O20: not countedQty. A qty-0 ad-spend row is $0, matching the backend.
export const adSpendUsd = (t: { metric_kind?: string | null; points: number; qty: number }) =>
  t.metric_kind === "ad_spend"
    ? (Number(t.points) || 0) * (Number(t.qty) || 0) * AD_SPEND_POINT_USD
    : null;

/** The webapp's seoBreakdownBucket: only SEO-ish buckets qualify, then the
 *  TASK TYPE decides onsite vs offsite. A generic "SEO" match gets no card. */
const OFFSITE_RE = /\boff[-\s]?site\s+seo\b/i;
const ONSITE_RE = /\bon[-\s]?site\s+seo\b/i;
const SEO_BUCKETS = new Set(["SEO", "Onsite SEO", "Offsite SEO"]);
function seoBreakdownBucket(t: PlannerTask): "onsite_seo" | "offsite_seo" | null {
  if (!SEO_BUCKETS.has(t.service_bucket)) return null;
  if (OFFSITE_RE.test(t.task_type)) return "offsite_seo";
  if (ONSITE_RE.test(t.task_type)) return "onsite_seo";
  return null;
}
/** One point is $100 — the Planner's POINTS_TO_DOLLARS. */
const POINTS_TO_DOLLARS = 100;
/** Whole dollars, matching how the Planner renders its spend cards. */
const usd = (n: number) =>
  `$${Math.round(Number(n) || 0).toLocaleString("en-US")}`;

function Stat({
  icon,
  label,
  value,
  tone,
  compact = false,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  tone: string;
  /** The webapp's second row of dollar cards is a size smaller. */
  compact?: boolean;
}) {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className={cn("flex items-center", compact ? "gap-2 p-3" : "gap-3 p-4")}>
        <span className={cn("grid place-items-center rounded-lg", compact ? "h-8 w-8 rounded-md" : "h-10 w-10", tone)}>{icon}</span>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className={cn("font-bold tabular-nums leading-tight text-foreground", compact ? "text-lg" : "text-2xl")}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlannerAgilePlanPage() {
  const { currentTenant } = useTenantStore();
  const [period, setPeriod] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [search, setSearch] = useState("");

  const { data: plan, isLoading: planLoading, isFetching: planFetching } = usePlan(currentTenant, period);
  // Recurrence looks BACK across periods, so it needs the unfiltered plan.
  const { data: allPlan } = usePlan(currentTenant);
  // Switching workspace changes the query key, so `plan` is briefly undefined
  // while the new client's plan loads — without this the page silently showed
  // the empty state as if the client had no tasks (Jacob, 2026-09-08).
  const switching = planLoading || (planFetching && !plan);
  const { data: catalog = [] } = useCatalog(true);
  const createTask = useCreateTask(currentTenant);
  const setStatus = useSetTaskStatus();
  const setBudget = useSetBudget(currentTenant);
  const deleteTask = useDeleteTask();
  // One push per task at a time, rows and bulk alike (E7); the busy state is per row.
  const { syncingIds, sync: syncTeamwork } = useTeamworkSync();
  const syncTask = (id: string) => void syncTeamwork(id).catch(() => undefined); // onError toasts

  const tasks = plan?.items ?? [];
  const allTasks = allPlan?.items ?? [];
  const sb = plan?.scoreboard;
  const cycleDay = plan?.billing_cycle_start_day;
  const budgetPlusCarry = sb?.budget_plus_carry ?? 0;
  const used = sb?.used ?? plan?.done_points ?? 0;
  const carryover = sb?.carryover ?? 0;
  const available = sb?.available ?? 0;

  // Editable monthly budget, synced to the scoreboard when the period changes.
  const [budgetInput, setBudgetInput] = useState("");
  useEffect(() => {
    setBudgetInput(sb ? String(round2(sb.budget)) : "");
  }, [sb?.budget, period]);
  const saveBudget = () => {
    if (!currentTenant) return;
    const val = Number(budgetInput);
    if (Number.isFinite(val) && val !== (sb?.budget ?? 0)) {
      setBudget.mutate({ billingPeriod: period, budgetPoints: val });
    }
  };

  // Parity pass 2: modal add/edit + bulk selection, as upstream.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PlannerTask | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rules, setRules] = useState<FilterRule[]>([]);
  // Upstream sorts on the first four columns only, toggling asc/desc with a
  // text arrow; unsorted is the fetch order.
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [noteTask, setNoteTask] = useState<{ task: PlannerTask; field: "notes" | "internal_notes" } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = q
      ? tasks.filter((t) =>
          `${t.task_type} ${t.notes} ${t.assignee ?? ""} ${t.internal_notes ?? ""}`
            .toLowerCase()
            .includes(q),
        )
      : tasks;
    const ruled = applyRules(searched, rules);
    if (!sort) return ruled;
    const val = (t: PlannerTask) => taskFieldValue(t, sort.key) ?? "";
    return [...ruled].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const cmp =
        typeof x === "number" && typeof y === "number"
          ? x - y
          : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [tasks, search, rules, sort]);

  const toggleSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key
        ? prev.dir === "asc"
          ? { key, dir: "desc" }
          : null
        : { key, dir: "asc" },
    );

  /** Upstream drags a 2px handle on the header edge; min 60px, not persisted. */
  const startResize = (key: string, e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget.parentElement as HTMLElement) ?? null;
    const startX = e.clientX;
    const startW = th?.getBoundingClientRect().width ?? 120;
    document.body.style.cursor = "col-resize";
    const move = (ev: PointerEvent) => {
      const next = Math.max(60, startW + (ev.clientX - startX));
      setWidths((w) => ({ ...w, [key]: next }));
    };
    const up = () => {
      document.body.style.cursor = "";
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const filteredIds = useMemo(() => filtered.map((t) => t.id), [filtered]);

  const monthLabel = useMemo(() => {
    const [y, m] = period.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [period]);

  const updateTask = useUpdateTask();
  const { data: archived = [] } = useArchivedTasks(currentTenant);
  const restoreTask = useRestoreTask();
  const [showArchive, setShowArchive] = useState(false);


  /** Assignee list (E5): Teamwork's active staff, plus every name already in
   *  use on this client's plan or as a catalog default (the whole list when
   *  Teamwork is unavailable), plus MISC. The dialog drops "(manual)" (O28)
   *  and adds Unassigned and "+ Custom…". */
  const { data: teamwork } = useAssignees();
  // People an admin marked inactive (O31/O38): hidden from new picks, also in
  // the Teamwork-down fallback. The dialog still keeps a task's own assignee.
  const inactiveAssignees = teamwork?.inactive_names ?? NO_NAMES;
  const assigneeOptions = useMemo(() => {
    const set_ = new Set<string>(["MISC"]);
    if (teamwork?.ok) for (const u of teamwork.users) set_.add(u.name);
    for (const t of allTasks) if (t.assignee) set_.add(t.assignee);
    for (const c of catalog) if (c.default_assignee) set_.add(c.default_assignee);
    for (const name of inactiveAssignees) set_.delete(name);
    return [...set_].sort((a, b) => a.localeCompare(b));
  }, [teamwork, allTasks, catalog, inactiveAssignees]);
  /** Teamwork name -> the admin's display name, where one is set. */
  const assigneeLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    if (teamwork?.ok) for (const u of teamwork.users) if (u.label && u.label !== u.name) labels[u.name] = u.label;
    return labels;
  }, [teamwork]);

  const periods = useMemo(() => {
    const set_ = new Set<string>([period, ...tasks.map((t) => t.billing_period)]);
    return [...set_].sort();
  }, [tasks, period]);

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });

  /** Upstream runs bulk verbs SEQUENTIALLY and emits ONE summary toast. */
  const runBulk = async (label: string, fn: (id: string) => Promise<unknown>) => {
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of selected) {
      try {
        await fn(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    if (failed) message.warning(`${label}: ${ok} done, ${failed} failed.`);
    else message.success(`${label}: ${ok} task(s)`);
  };

  const submitTask = (d: TaskDraft) => {
    const body = {
      task_type: d.task_type.trim(),
      service_bucket: d.service_bucket || undefined,
      // The dialog holds the row total; the API and Core store per unit (O3).
      points: toPerUnit(d.points, d.qty),
      qty: d.qty,
      status: d.status,
      priority: d.priority,
      // "" is a real value: Unassigned saves an empty assignee (O28); a null
      // would be skipped by the legacy Core update.
      assignee: d.assignee,
      due_date: d.due_date || null,
      notes: d.notes,
      internal_notes: d.internal_notes,
      is_recurring: d.is_recurring,
    };
    if (editing) {
      updateTask.mutate({ id: editing.id, body }, { onSuccess: () => setDialogOpen(false) });
    } else {
      createTask.mutate(
        createBody({ billing_period: d.billing_period, ...body }),
        { onSuccess: () => setDialogOpen(false) },
      );
    }
  };

  const effort = (t: PlannerTask) => toTotal(t.points, t.qty);

  /** Upstream's rule: a recurring task is DUE when the month gap from its
   *  latest prior occurrence is a positive whole multiple of its cadence,
   *  and no task of the same type already exists in the target period.
   *  A NULL cadence means monthly (the legacy boolean's meaning). */
  const monthsBetween = (a: string, b: string) => {
    const [ay, am] = a.split("-").map(Number);
    const [by, bm] = b.split("-").map(Number);
    return (by - ay) * 12 + (bm - am);
  };
  const dueRecurring = useMemo(() => {
    const here = new Set(tasks.map((t) => t.task_type.trim().toLowerCase()));
    const latest = new Map<string, PlannerTask>();
    for (const t of allTasks) {
      if (!t.is_recurring) continue;
      if (t.billing_period >= period) continue; // only PRIOR occurrences
      const key = t.task_type.trim().toLowerCase();
      const prev = latest.get(key);
      if (!prev || t.billing_period > prev.billing_period) latest.set(key, t);
    }
    const due: PlannerTask[] = [];
    for (const [key, t] of latest) {
      if (here.has(key)) continue; // already added this period
      const gap = monthsBetween(t.billing_period, period);
      const cadence = t.recurrence_months || 1;
      if (gap > 0 && gap % cadence === 0) due.push(t);
    }
    return due;
  }, [allTasks, tasks, period]);

  const copyRecurring = async () => {
    setBulkBusy(true);
    let n = 0;
    for (const t of dueRecurring) {
      try {
        await createTask.mutateAsync(createBody({
          billing_period: period,
          task_type: t.task_type,
          qty: countedQty(t.qty),
          points: t.points, // already per unit (stored), so no conversion
          service_bucket: t.service_bucket,
          assignee: t.assignee,
          notes: t.notes,
          internal_notes: t.internal_notes,
          is_recurring: true, // dueRecurring holds only recurring tasks
        }));
        n += 1;
      } catch {
        /* one failure must not stop the rest */
      }
    }
    setBulkBusy(false);
    if (n) message.success(`${n} recurring task(s) added to ${monthLabel}.`);
  };

  // Onsite/Offsite SEO dollar cards, computed from this period's rows exactly
  // as the webapp does (bucket qualifies, task type decides the side).
  const seoDollars = useMemo(() => {
    let onsite = 0;
    let offsite = 0;
    for (const t of tasks) {
      const side = seoBreakdownBucket(t);
      if (side === "onsite_seo") onsite += effort(t) * POINTS_TO_DOLLARS;
      else if (side === "offsite_seo") offsite += effort(t) * POINTS_TO_DOLLARS;
    }
    return { onsite, offsite };
  }, [tasks]);

  /** Header cell: sortable on the upstream four, resizable on all. */
  const Th = ({
    label,
    sortKey,
    className,
    width,
  }: {
    label: string;
    sortKey?: string;
    className?: string;
    width?: number;
  }) => (
    <TableHead
      className={cn("relative select-none", className)}
      style={widths[label] || width ? { width: widths[label] ?? width } : undefined}
    >
      {sortKey ? (
        <button
          type="button"
          onClick={() => toggleSort(sortKey)}
          className="flex items-center gap-1 text-left uppercase"
        >
          {label}
          {sort?.key === sortKey ? <span className="ml-0.5">{sort.dir === "asc" ? "↑" : "↓"}</span> : null}
        </button>
      ) : (
        label
      )}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label} column`}
        onPointerDown={(e) => startResize(label, e)}
        className="absolute -right-1 top-0 bottom-0 z-10 w-2 cursor-col-resize hover:bg-coral/30 active:bg-coral/50"
      />
    </TableHead>
  );

  return (
    <div className="space-y-4 p-6">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <ClipboardList className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Agile Plan</h1>
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            {currentTenant || "Select a client"} — monthly work plan
            {switching ? (
              <span className="inline-flex items-center gap-1 text-xs text-coral">
                <Loader2 className="h-3 w-3 animate-spin" /> loading…
              </span>
            ) : null}
            {cycleDay && plan?.contract_id ? (
              // S3: read-only; the anchor is edited on the client's Contract tab.
              <Link
                to={`/clients/c/${plan.contract_id}`}
                className={cn(badgeVariants({ variant: "outline" }), "font-medium text-muted-foreground hover:border-coral hover:text-coral-dark")}
                title="Edit the billing day on the client's Contract tab"
              >
                Bills on the {cycleDay === 15 ? "15th" : "1st"} · {formatCycleRange(period, cycleDay)}
              </Link>
            ) : null}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setPeriod((p) => shiftMonth(p, -1))} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-semibold">
            <CalendarDays className="h-4 w-4 text-muted-foreground" /> {monthLabel}
          </span>
          <Button variant="outline" size="icon" onClick={() => setPeriod((p) => shiftMonth(p, 1))} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1">
            <span className="text-xs font-semibold text-muted-foreground">Budget</span>
            <Input
              type="number"
              min={0}
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              onBlur={saveBudget}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              className="h-7 w-16 border-0 p-0 text-right font-semibold tabular-nums"
              aria-label="Monthly budget (points)"
            />
            <span className="text-xs text-muted-foreground">pts</span>
          </div>
        </div>
      </div>

      <CollapsibleSection title="Scoreboard">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat icon={<Wallet className="h-5 w-5 text-coral-dark" />} label="Budget + Carry" value={round2(budgetPlusCarry)} tone="bg-coral-light" />
          <Stat icon={<Flame className="h-5 w-5 text-coral-dark" />} label="Used" value={round2(used)} tone="bg-coral-light" />
          <Stat icon={<RefreshCw className="h-5 w-5 text-teal-dark" />} label="Carryover" value={round2(carryover)} tone="bg-teal-light" />
          <Stat
            icon={<CheckCircle2 className={cn("h-5 w-5", available < 0 ? "text-destructive" : "text-teal-dark")} />}
            label="Available"
            value={<span className={cn(available < 0 && "text-destructive")}>{round2(available)}</span>}
            tone={available < 0 ? "bg-destructive/10" : "bg-teal-light"}
          />
        </div>
        {/* Dollar row. Media spend comes from metric_kind='ad_spend' (1 pt =
            $100 — the bucket alone cannot separate media from the management
            fee beside it); the SEO pair is split by task type, as upstream. */}
        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat icon={<DollarSign className="h-4 w-4 text-coral-dark" />} label="Google Ad Spend" value={usd(sb?.google_ad_spend ?? 0)} tone="bg-coral-light" compact />
          <Stat icon={<DollarSign className="h-4 w-4 text-teal-dark" />} label="Meta Ad Spend" value={usd(sb?.meta_ad_spend ?? 0)} tone="bg-teal-light" compact />
          <Stat icon={<FileText className="h-4 w-4 text-teal-dark" />} label="Onsite SEO" value={usd(seoDollars.onsite)} tone="bg-teal-light" compact />
          <Stat icon={<LinkIcon className="h-4 w-4 text-coral-dark" />} label="Offsite SEO" value={usd(seoDollars.offsite)} tone="bg-coral-light" compact />
        </div>
      </CollapsibleSection>

      <RunningNotesCard tenantId={currentTenant} />

      <CollapsibleSection title="Tasks">
      <Card className="border-0 shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search task type, notes, assignee…" className="h-9 w-56 pl-8" />
          </div>
          <TaskFilter rules={rules} onChange={setRules} />
          <span className="text-xs tabular-nums text-muted-foreground">
            {search.trim() || rules.length > 0
              ? `${filtered.length} of ${tasks.length}`
              : `${tasks.length} ${tasks.length === 1 ? "task" : "tasks"}`}
          </span>
          {search.trim() || rules.length > 0 ? (
            <button type="button" onClick={() => { setSearch(""); setRules([]); }} className="text-xs text-coral-dark hover:underline">
              Clear
            </button>
          ) : null}
          {/* Upstream's toolbar ends with ONE action: + Add Task, which opens
              the modal. The old inline catalog-select + qty + add trio is
              gone — it left a SECOND, disabled "Add Task" button that did
              nothing until a type was picked (exactly the "nothing happens"
              Jacob hit). */}
          <div className="ml-auto flex items-end gap-2">
            {dueRecurring.length > 0 ? (
              <Button
                variant="outline"
                className="h-9"
                disabled={bulkBusy}
                title={`Copy ${dueRecurring.length} recurring task(s) due this month`}
                onClick={copyRecurring}
              >
                {bulkBusy ? "Copying…" : `Copy recurring tasks (${dueRecurring.length})`}
              </Button>
            ) : null}
            <Button
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="h-9"
              aria-label="add task"
            >
              <Plus className="mr-1 h-4 w-4" /> Add Task
            </Button>
          </div>
        </div>

        {/* Grid */}
        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-coral-light/60 px-4 py-2">
            <span className="text-sm font-medium text-foreground">{selected.size} selected</span>
            <Button variant="outline" size="sm" className="h-7" disabled={bulkBusy}
              onClick={() => runBulk("Marked done", (id) => setStatus.mutateAsync({ id, status: "done" }))}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Mark Done
            </Button>
            <Button variant="outline" size="sm" className="h-7" disabled={bulkBusy}
              onClick={() => runBulk("Marked pending", (id) => setStatus.mutateAsync({ id, status: "not_started" }))}>
              Mark Pending
            </Button>
            <Button variant="outline" size="sm" className="h-7" disabled={bulkBusy}
              onClick={() => runBulk("Synced", syncTeamwork)}>
              <Send className="mr-1 h-3.5 w-3.5" /> Sync to Teamwork
            </Button>
            <Select onValueChange={(v) => runBulk("Priority updated", (id) => updateTask.mutateAsync({ id, body: { priority: v } }))}>
              <SelectTrigger className="h-7 w-[140px] text-sm"><SelectValue placeholder="Set priority…" /></SelectTrigger>
              <SelectContent>
                {["none", "low", "medium", "high"].map((p) => (
                  <SelectItem key={p} value={p}>{p === "none" ? "None" : p[0].toUpperCase() + p.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm"
              className="h-7 border-destructive/30 text-destructive hover:bg-destructive/10"
              disabled={bulkBusy}
              onClick={() => {
                if (window.confirm(`Delete ${selected.size} task(s)? Synced tasks are removed from Teamwork too.`)) {
                  runBulk("Deleted", (id) => deleteTask.mutateAsync(id));
                }
              }}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
            <Button variant="ghost" size="sm" className="ml-auto h-7 text-muted-foreground"
              onClick={() => setSelected(new Set())}>
              {bulkBusy ? "Working…" : "Clear"}
            </Button>
          </div>
        ) : null}

        {/* Mobile card view (< md), as upstream's task-grid: same filtered +
            sorted rows as the table, one MobileTaskCard each. The toolbar and
            bulk bar above are shared; only the grid swaps. */}
        <div className="md:hidden space-y-2 p-3">
          {switching ? (
            [0, 1, 2, 3].map((i) => (
              <div key={`msk-${i}`} className="h-16 w-full animate-pulse rounded-lg bg-muted" />
            ))
          ) : filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">No tasks yet</p>
          ) : (
            <>
              <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all tasks" />
                Select all
              </label>
              {filtered.map((t) => (
                <MobileTaskCard
                  key={`mobile-${t.id}`}
                  task={t}
                  selected={selected.has(t.id)}
                  onSelectChange={() => toggleRow(t.id)}
                  onStatusChange={(status) => setStatus.mutate({ id: t.id, status })}
                  syncing={syncingIds.has(t.id)}
                  onSync={() => syncTask(t.id)}
                  onEdit={() => { setEditing(t); setDialogOpen(true); }}
                  // The card confirms in place (two-step), so no window.confirm here.
                  onDelete={() => deleteTask.mutate(t.id)}
                />
              ))}
            </>
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="w-[40px]">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all tasks" />
                </TableHead>
                <Th label="Period" sortKey="billing_period" width={115} />
                <Th label="Task Type" sortKey="task_type" width={170} />
                <Th label="Pts" sortKey="points" width={55} />
                <Th label="Qty" sortKey="qty" width={55} />
                <Th label="Bucket" width={110} />
                <Th label="Assignee" width={200} />
                <Th label="Due Date" width={115} />
                <Th label="Status" width={100} />
                <Th label="Sync" width={55} className="text-center" />
                <Th label="TW" width={80} />
                <Th label="Notes" width={140} />
                <Th label="Internal" width={140} />
                <TableHead className="w-[65px] text-right" aria-label="Row actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {switching ? (
                [0, 1, 2, 3].map((i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={15} className="py-3">
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} className="py-16 text-center text-sm text-muted-foreground">
                    No tasks yet
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((t) => (
                  <TableRow
                    key={t.id}
                    className={cn(
                      "transition-colors",
                      t.service_bucket === "One-Time"
                        ? "bg-destructive/5 hover:bg-destructive/10"
                        : "hover:bg-muted/50",
                    )}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(t.id)}
                        onCheckedChange={() => toggleRow(t.id)}
                        aria-label={`Select ${t.task_type}`}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                      {t.billing_period}
                    </TableCell>
                    <TableCell className="font-medium">
                      {/* Priority chip lives INSIDE the type cell, as upstream. */}
                      {t.priority && t.priority !== "none" ? (
                        <span className={cn("mr-1.5 inline-block rounded px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide", PRIORITY_CHIP[t.priority])} title={`${t.priority} priority`}>
                          {t.priority}
                        </span>
                      ) : null}
                      {t.task_type}
                    </TableCell>
                    <TableCell className="font-semibold tabular-nums">{toTotal(t.points, t.qty)}</TableCell>
                    <TableCell className="tabular-nums">
                      {round2(t.qty)}
                      {adSpendUsd(t) !== null && (
                        <span
                          className="ml-1.5 text-xs text-muted-foreground"
                          title="Media spend: points × qty × $100"
                        >
                          {usd(adSpendUsd(t) as number)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("font-normal", BUCKET_STYLE[t.service_bucket])}>
                        {t.service_bucket}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{assigneeLabel(t.assignee)}</TableCell>
                    <TableCell className="text-muted-foreground">{t.due_date || "—"}</TableCell>
                    <TableCell>
                      <Select
                        value={t.status === "done" ? "Done" : "Pending"}
                        onValueChange={(v) =>
                          setStatus.mutate({ id: t.id, status: v === "Done" ? "done" : "not_started" })
                        }
                      >
                        <SelectTrigger
                          className={cn(
                            "h-8 w-[104px] border-0 font-semibold",
                            t.status === "done" ? "bg-teal-light text-teal-dark" : "bg-coral-light text-coral-dark",
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Done">Done</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      {syncingIds.has(t.id) ? (
                        <button type="button" disabled aria-busy="true" aria-label="Syncing…" title="Syncing…">
                          <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />
                        </button>
                      ) : t.sync_status === "synced" ? (
                        <Check className="mx-auto h-4 w-4 text-teal-dark" aria-label="Synced" />
                      ) : t.sync_status === "pending" ? (
                        <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" aria-label="Sync pending" />
                      ) : t.sync_status === "failed" ? (
                        <button
                          type="button"
                          title="Sync failed — click to retry"
                          aria-label="Retry sync"
                          onClick={() => syncTask(t.id)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <AlertTriangle className="mx-auto h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Sync to Teamwork"
                          aria-label="Sync to Teamwork"
                          onClick={() => syncTask(t.id)}
                          className="text-muted-foreground hover:text-teal-dark"
                        >
                          <CloudUpload className="mx-auto h-4 w-4" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {t.teamwork_task_id ? (
                        <a
                          href={`https://teamwork.agileandco.com/app/tasks/${t.teamwork_task_id}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-coral-dark hover:underline"
                        >
                          {t.teamwork_task_id}
                        </a>
                      ) : (
                        <span className="italic text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <button
                        type="button"
                        onClick={() => setNoteTask({ task: t, field: "notes" })}
                        className="block w-full truncate text-left text-muted-foreground hover:text-foreground"
                        title={t.notes}
                      >
                        {t.notes || <span className="italic text-muted-foreground/70">Add note...</span>}
                      </button>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <button
                        type="button"
                        onClick={() => setNoteTask({ task: t, field: "internal_notes" })}
                        className="block w-full truncate text-left text-muted-foreground hover:text-foreground"
                        title={t.internal_notes}
                      >
                        {t.internal_notes || <span className="italic text-muted-foreground/70">Internal...</span>}
                      </button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {/* The Planner webapp's per-task sync button. The server
                          skips already-synced tasks, so this is always safe. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs text-muted-foreground hover:text-coral-dark"
                        onClick={() => { setEditing(t); setDialogOpen(true); }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-teal-dark hover:bg-teal-light"
                        title={syncingIds.has(t.id) ? "Syncing…" : "Sync to Teamwork"}
                        aria-label={syncingIds.has(t.id) ? "Syncing…" : "Sync to Teamwork"}
                        aria-busy={syncingIds.has(t.id) || undefined}
                        disabled={syncingIds.has(t.id)}
                        onClick={() => syncTask(t.id)}
                      >
                        {syncingIds.has(t.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                        title="Delete task"
                        aria-label="Delete task"
                        disabled={deleteTask.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete "${t.task_type}"? A synced task is removed from Teamwork too.`)) {
                            deleteTask.mutate(t.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      </CollapsibleSection>

      {archived.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <button
            type="button"
            onClick={() => setShowArchive((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/50"
          >
            <span className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Archive ({archived.length})</span>
              <span className="text-xs text-muted-foreground">
                — tasks deleted in Teamwork or the planner
              </span>
            </span>
            <span className="text-xs text-muted-foreground">{showArchive ? "Hide" : "Show"}</span>
          </button>
          {showArchive ? (
            <div className="overflow-x-auto border-t border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Task</TableHead>
                    <TableHead className="w-[110px]">Period</TableHead>
                    <TableHead className="w-[80px] text-right">Points</TableHead>
                    <TableHead className="w-[100px]">Reason</TableHead>
                    <TableHead className="w-[180px]">Archived</TableHead>
                    <TableHead className="w-[90px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archived.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.task_type}</TableCell>
                      <TableCell className="tabular-nums text-muted-foreground">{a.billing_period}</TableCell>
                      <TableCell className="text-right tabular-nums">{toTotal(a.points, a.qty)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs font-normal">
                          {a.deleted_reason === "teamwork" ? "Teamwork" : "User"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {a.deleted_at ? new Date(a.deleted_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          type="button"
                          onClick={() => restoreTask.mutate(a.id)}
                          disabled={restoreTask.isPending}
                          className="text-xs font-medium text-coral-dark hover:underline"
                        >
                          Restore
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </Card>
      ) : null}

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        period={period}
        periods={periods}
        catalog={catalog}
        existingTypes={tasks.map((t) => t.task_type)}
        assignees={assigneeOptions}
        assigneeLabels={assigneeLabels}
        inactiveAssignees={inactiveAssignees}
        task={editing}
        saving={createTask.isPending || updateTask.isPending}
        onSubmit={submitTask}
      />

      <TaskNoteDialog
        open={noteTask != null}
        onOpenChange={(o) => { if (!o) setNoteTask(null); }}
        title={noteTask?.field === "internal_notes" ? "Internal note" : "Client note"}
        value={noteTask ? (noteTask.field === "internal_notes" ? noteTask.task.internal_notes : noteTask.task.notes) ?? "" : ""}
        saving={updateTask.isPending}
        onSave={(next) => {
          if (!noteTask) return;
          updateTask.mutate(
            { id: noteTask.task.id, body: { [noteTask.field]: next } },
            { onSuccess: () => setNoteTask(null) },
          );
        }}
      />
    </div>
  );
}
